import { FC } from 'react';
import {
  Box,
  InputBase,
  MenuItem,
  Switch,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';

import { BorderedSelect } from '@components';
import { PHONE_FORMAT_EXAMPLE } from '@libs/constants';
import { useTrans } from '@libs/i18n';

import { coerceScalar, examplePlaceholder } from '../../utils/fieldValue';
import { editBoxSx, ORG_TOKENS } from '../../utils/tokens';

import type { FieldInputProps } from './fieldInput.types';

const TEXTUAL = new Set(['string', 'email', 'phone', 'number', 'date']);

/** The dark value text shown in read mode (or a muted placeholder hint). */
const ReadValue: FC<{
  value: string;
  empty: boolean;
  placeholder: string;
  textarea?: boolean;
}> = observer(({ value, empty, placeholder, textarea }) => (
  <Typography
    variant="textSM_medium"
    color={empty ? 'text.disabled' : 'text.primary'}
    sx={{
      fontStyle: empty ? 'italic' : 'normal',
      whiteSpace: textarea ? 'pre-wrap' : 'normal',
      wordBreak: 'break-word',
      // Long textarea content is capped to a fixed height and scrolls instead
      // of expanding the whole card.
      ...(textarea &&
        !empty && {
          maxHeight: 160,
          overflowY: 'auto',
          width: '100%',
        }),
    }}
  >
    {empty ? placeholder : value}
  </Typography>
));

/**
 * Renderer for scalar field types: string / email / phone / number / date /
 * textarea / boolean / select. Read mode shows the value as plain text; edit
 * mode swaps in a bordered input that tints blue while the value is unsaved.
 */
export const ScalarField: FC<FieldInputProps> = observer(
  ({ field, value, mode, dirty, disabled, onChange }) => {
    const { t } = useTrans();
    const isTextarea = field.type === 'textarea';
    const isBoolean = field.type === 'boolean';
    const isSelect = field.type === 'select';
    const empty = value == null || value === '';
    // Prefer the field's authoring example as the hint; for a phone show the
    // accepted format so the shape is visible before the user trips validation;
    // otherwise fall back to the generic "add manually" text.
    const placeholder =
      examplePlaceholder(field.example) ??
      (field.type === 'phone'
        ? PHONE_FORMAT_EXAMPLE
        : t('app.orgProfile.field.addManually'));

    const boxSx = {
      ...editBoxSx(dirty),
      px: 1.5,
      py: isTextarea ? 1.25 : 1.1,
      display: 'flex',
      gap: 1,
      alignItems: isTextarea ? 'flex-start' : 'center',
      minHeight: isTextarea ? 72 : 'auto',
    } as const;

    // ── Boolean ──────────────────────────────────────────────────────────
    if (isBoolean) {
      const text =
        value === true
          ? t('app.orgProfile.field.yes')
          : value === false
            ? t('app.orgProfile.field.no')
            : '—';
      if (mode === 'read') {
        return <ReadValue value={text} empty={value == null} placeholder={placeholder} />;
      }
      return (
        <Box sx={{ ...boxSx, alignItems: 'center' }}>
          <Switch
            size="small"
            checked={value === true}
            disabled={disabled}
            onChange={(e) => onChange?.(e.target.checked)}
          />
          <Typography variant="textSM_regular" color="text.primary">{text}</Typography>
        </Box>
      );
    }

    // ── Select ───────────────────────────────────────────────────────────
    if (isSelect) {
      if (mode === 'read') {
        return (
          <ReadValue value={String(value ?? '')} empty={empty} placeholder={placeholder} />
        );
      }
      return (
        <BorderedSelect
          variant="field"
          fullWidth
          displayEmpty
          dirty={dirty}
          disabled={disabled}
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onChange?.((e.target.value as string) || null)}
        >
          <MenuItem value="">
            <em>—</em>
          </MenuItem>
          {(field.options ?? []).map((o) => (
            <MenuItem key={o} value={o} sx={{ typography: 'textSM_regular' }}>
              {o}
            </MenuItem>
          ))}
        </BorderedSelect>
      );
    }

    // ── Date — calendar popover (read mode shows a friendly format) ───────
    if (field.type === 'date') {
      const raw = typeof value === 'string' ? value : '';
      const parsed = raw ? dayjs(raw) : null;
      const valid = parsed?.isValid() ?? false;

      if (mode === 'read') {
        return (
          <ReadValue
            value={valid ? (parsed as dayjs.Dayjs).format('MMM D, YYYY') : raw}
            empty={empty}
            placeholder={placeholder}
          />
        );
      }
      return (
        <Box sx={boxSx}>
          <DatePicker
            value={valid ? parsed : null}
            disabled={disabled}
            format="MMM D, YYYY"
            onChange={(next) =>
              onChange?.(next && next.isValid() ? next.format('YYYY-MM-DD') : null)
            }
            slotProps={{
              // The format mask ("MMM D, YYYY") doubles as the empty-state hint.
              // The outer box already supplies the border, so strip the field's
              // own standard-variant underline.
              textField: {
                fullWidth: true,
                variant: 'standard',
                sx: {
                  typography: 'textSM_regular',
                  '& .MuiPickersInputBase-root::before, & .MuiPickersInputBase-root::after':
                    { borderBottom: 'none' },
                  '& .MuiInput-root::before, & .MuiInput-root::after': {
                    borderBottom: 'none',
                  },
                  '& .MuiPickersSectionList-root, & input': { p: 0 },
                },
              },
              // The calendar popup ships without elevation in this app's theme,
              // which makes it look flat/broken against the page. Give the popup
              // surface a soft shadow + rounded corners.
              desktopPaper: {
                sx: {
                  borderRadius: `${ORG_TOKENS.radiusMd}px`,
                  boxShadow: '0px 8px 28px rgba(0, 0, 0, 0.16)',
                  border: 1,
                  borderColor: 'grey.300',
                },
              },
            }}
            sx={{ width: '100%' }}
          />
        </Box>
      );
    }

    // ── Text-like (string / email / phone / number / textarea) ────────────
    const stringValue =
      value == null ? '' : typeof value === 'string' ? value : String(value);
    const inputType =
      field.type === 'number'
        ? 'number'
        : field.type === 'email'
          ? 'email'
          : field.type === 'phone'
            ? 'tel'
            : 'text';

    if (mode === 'read') {
      return (
        <ReadValue
          value={stringValue}
          empty={empty}
          placeholder={placeholder}
          textarea={isTextarea}
        />
      );
    }

    return (
      <Box sx={boxSx}>
        <InputBase
          fullWidth
          multiline={isTextarea}
          minRows={isTextarea ? 3 : undefined}
          // Cap the editable textarea so it scrolls past ~6 rows instead of
          // pushing the form open indefinitely.
          maxRows={isTextarea ? 6 : undefined}
          type={isTextarea ? undefined : inputType}
          value={stringValue}
          disabled={disabled}
          placeholder={placeholder}
          onChange={(e) =>
            onChange?.(
              TEXTUAL.has(field.type)
                ? coerceScalar(field.type, e.target.value)
                : e.target.value,
            )
          }
          sx={{
            typography: 'textSM_regular',
            // The value the user typed reads as dark text; the placeholder keeps
            // the muted tone so a filled field is obvious against an empty one.
            color: 'text.primary',
            p: 0,
            '& input::placeholder, & textarea::placeholder': {
              color: 'text.tertiary',
              opacity: 1,
            },
          }}
        />
      </Box>
    );
  },
);
